import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "btn-base outline-none cursor-pointer focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 'btn-enabled hover:bg-swd-petrol-grau/90',
        enabled: 'btn-enabled hover:bg-swd-petrol-grau/90',
        disabled: 'btn-disabled',
        error: 'btn-error hover:bg-red-700',
        outline: 'btn-outlined hover:opacity-80',
        'outline-disabled': 'btn-outlined-disabled',
        'outline-error': 'btn-outlined-error hover:bg-red-50',
        // Keep some shadcn variants for backward compatibility
        destructive: 'btn-error hover:bg-red-700',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'btn-large btn-large-text',
        sm: 'btn-small btn-small-text',
        lg: 'btn-large btn-large-text',
        icon: 'size-12 px-0',
      },
      rounded: {
        default: '', // Uses the default rounded from btn-large/btn-small classes
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  rounded,
  asChild = false,
  loading = false,
  ...props
}: React.ComponentProps<'button'> &
  Omit<VariantProps<typeof buttonVariants>, 'rounded'> & {
    asChild?: boolean;
    loading?: boolean;
    rounded?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  // Auto-switch to disabled variant when disabled or loading
  const effectiveVariant =
    loading || props.disabled
      ? variant === 'outline'
        ? 'outline-disabled'
        : variant === 'error'
          ? 'outline-error'
          : 'disabled'
      : variant;

  const roundedVariant = rounded ? 'full' : 'default';

  return (
    <Comp
      data-slot='button'
      className={cn(
        buttonVariants({
          variant: effectiveVariant,
          size,
          rounded: roundedVariant,
          className,
        })
      )}
      aria-busy={loading}
      disabled={loading || props.disabled}
      {...props}
    />
  );
}

export { Button, buttonVariants };
